import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { AlertTriangle, RefreshCw, ChevronRight, ArrowRight } from "lucide-react";
import { useAutoRefresh } from "../hooks/useAutoRefresh";

export default function AlertsPage() {
  const [data, setData] = useState(null);

  const load = () => api.get("/alerts").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useAutoRefresh(load, 30000);
  if (!data) return <div className="text-slate-500">Memuat...</div>;

  const criticalCount = data.life_limit.length;
  const rotationCount = data.rotation_due.length;

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-display text-3xl font-black text-slate-900">Alert & Rotasi Ban</h1>
        <p className="text-slate-500 text-sm mt-1">
          Batas umur: <span className="font-mono">{data.config.km_life_limit.toLocaleString()} km</span> ·
          Rotasi truck: <span className="font-mono">{data.config.rotation_km_interval.toLocaleString()} km</span> ·
          Rotasi trailer: <span className="font-mono">{data.config.rotation_days_interval} hari</span>
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section
          title="Batas Umur Ban Tercapai"
          count={criticalCount}
          icon={AlertTriangle}
          empty="Tidak ada ban yang mendekati batas umur."
          items={data.life_limit}
          testId="section-life"
          accent="border-red-600 bg-red-50"
        />
        <Section
          title="Ban Perlu Rotasi"
          count={rotationCount}
          icon={RefreshCw}
          empty="Semua ban dalam interval rotasi aman."
          items={data.rotation_due}
          testId="section-rotation"
          accent="border-amber-600 bg-amber-50"
        />
      </div>
    </div>
  );
}

function Section({ title, count, icon: Icon, empty, items, accent, testId }) {
  return (
    <div className="border-2 border-slate-200 bg-white rounded-sm" data-testid={testId}>
      <div className={`p-4 border-b-2 ${accent} flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-slate-900" />
          <div className="font-display font-black text-slate-900">{title}</div>
        </div>
        <div className="font-mono font-bold text-2xl text-slate-900">{count}</div>
      </div>
      <div className="divide-y divide-slate-200">
        {items.length === 0 ? (
          <div className="p-6 text-sm text-slate-500 text-center">{empty}</div>
        ) : (
          items.map((it) => (
            <Link
              to={`/vehicles/${it.vehicle_type}/${it.vehicle_id}`}
              key={`${it.tyre_id}-${it.position}`}
              data-testid={`alert-row-${it.serial_short}`}
              className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-slate-900">{it.serial_short}</span>
                  <span className="text-xs text-slate-500">·</span>
                  <span className="text-xs text-slate-600">{it.brand}</span>
                  <SeverityBadge s={it.severity} />
                </div>
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                  <span className="uppercase tracking-wider font-bold">
                    {it.vehicle_type === "truck" ? "Truck" : "Trailer"} {it.vehicle_label}
                  </span>
                  <ArrowRight className="w-3 h-3" />
                  <span className="font-mono">{it.position}</span>
                </div>
                <div className="text-xs text-slate-700 mt-1">{it.reason}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
            </Link>
          ))
        )}
      </div>
    </div>
  );
}

function SeverityBadge({ s }) {
  const map = {
    critical: "bg-red-600 text-white",
    warning: "bg-amber-500 text-slate-900",
    info: "bg-slate-900 text-orange-400",
  };
  return (
    <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm ${map[s] || ""}`}>
      {s}
    </span>
  );
}
